/**
 * Testimonial Carousel
 * Supports: infinite scroll, autoplay, arrows, dots, touch/swipe, responsive slides-per-view
 */
(function () {
  class TestimonialCarousel {
    constructor(el) {
      this.section = el;
      this.track = el.querySelector('.tc-carousel-track');
      this.cards = Array.from(this.track.querySelectorAll('.tc-card'));

      if (this.cards.length === 0) return;

      // Read settings from data attributes
      this.autoplay = this.section.dataset.autoplay === 'true';
      this.autoplaySpeed = parseInt(this.section.dataset.autoplaySpeed, 10) || 5000;
      this.infinite = this.section.dataset.infinite === 'true';
      this.showArrows = this.section.dataset.arrows === 'true';
      this.showDots = this.section.dataset.dots === 'true';
      this.desktopSlides = parseInt(this.section.dataset.desktopSlides, 10) || 3;
      this.tabletSlides = parseInt(this.section.dataset.tabletSlides, 10) || 2;
      this.mobileSlides = parseInt(this.section.dataset.mobileSlides, 10) || 1;

      this.currentIndex = 0;
      this.isTransitioning = false;
      this.autoplayTimer = null;
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.totalOriginal = this.cards.length;

      // Elements
      this.prevBtn = el.querySelector('.tc-arrow--prev');
      this.nextBtn = el.querySelector('.tc-arrow--next');
      this.dotsContainer = el.querySelector('.tc-dots');
      this.arrowsContainer = el.querySelector('.tc-nav-arrows');

      this.init();
    }

    getSlidesPerView() {
      const w = window.innerWidth;
      if (w <= 767) return this.mobileSlides;
      if (w <= 991) return this.tabletSlides;
      return this.desktopSlides;
    }

    init() {
      this.slidesPerView = this.getSlidesPerView();

      // Setup infinite clones
      if (this.infinite && this.totalOriginal > this.slidesPerView) {
        this.setupInfiniteClones();
      }

      this.allCards = Array.from(this.track.querySelectorAll('.tc-card'));
      this.totalSlides = this.allCards.length;

      this.updateCardWidths();
      this.buildDots();
      this.updateUI();

      // If infinite, start at the first "real" slide
      if (this.infinite && this.totalOriginal > this.slidesPerView) {
        this.currentIndex = this.totalOriginal;
        this.jumpToSlide(this.currentIndex);
      }

      // Bind events
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

      // Touch events
      this.track.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: true });
      this.track.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true });

      // Resize handler
      this._resizeHandler = this.debounce(() => this.onResize(), 200);
      window.addEventListener('resize', this._resizeHandler);

      // Transition end for infinite loop reset
      this.track.addEventListener('transitionend', () => this.onTransitionEnd());

      // Start autoplay
      if (this.autoplay) this.startAutoplay();

      // Pause autoplay on hover
      this.section.addEventListener('mouseenter', () => this.stopAutoplay());
      this.section.addEventListener('mouseleave', () => { if (this.autoplay) this.startAutoplay(); });

      // Toggle arrow/dot visibility
      this.toggleControls();
    }

    setupInfiniteClones() {
      // Clone all slides and append/prepend for seamless infinite loop
      const clonesBefore = [];
      const clonesAfter = [];

      this.cards.forEach((card) => {
        const cloneAfter = card.cloneNode(true);
        cloneAfter.classList.add('tc-clone');
        cloneAfter.setAttribute('aria-hidden', 'true');
        clonesAfter.push(cloneAfter);
      });

      this.cards.forEach((card) => {
        const cloneBefore = card.cloneNode(true);
        cloneBefore.classList.add('tc-clone');
        cloneBefore.setAttribute('aria-hidden', 'true');
        clonesBefore.push(cloneBefore);
      });

      // Prepend clones
      clonesBefore.reverse().forEach((clone) => {
        this.track.insertBefore(clone, this.track.firstChild);
      });

      // Append clones
      clonesAfter.forEach((clone) => {
        this.track.appendChild(clone);
      });
    }

    updateCardWidths() {
      this.slidesPerView = this.getSlidesPerView();
      const pct = 100 / this.slidesPerView;
      this.allCards = Array.from(this.track.querySelectorAll('.tc-card'));
      this.allCards.forEach((card) => {
        card.style.width = pct + '%';
      });
    }

    buildDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';

      const dotCount = Math.max(1, this.totalOriginal - this.slidesPerView + 1);
      for (let i = 0; i < dotCount; i++) {
        const dot = document.createElement('button');
        dot.classList.add('tc-dot');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.style.backgroundColor = this.section.dataset.dotColor || '#1a3328';
        dot.addEventListener('click', () => {
          const targetIndex = this.infinite && this.totalOriginal > this.slidesPerView
            ? i + this.totalOriginal
            : i;
          this.goToSlide(targetIndex);
        });
        this.dotsContainer.appendChild(dot);
      }
    }

    updateUI() {
      // Update dots
      if (this.dotsContainer) {
        const dots = this.dotsContainer.querySelectorAll('.tc-dot');
        let realIndex = this.getRealIndex();
        const maxDotIndex = Math.max(0, this.totalOriginal - this.slidesPerView);
        if (realIndex > maxDotIndex) realIndex = maxDotIndex;
        if (realIndex < 0) realIndex = 0;

        dots.forEach((dot, i) => {
          dot.classList.toggle('tc-dot--active', i === realIndex);
          dot.style.opacity = i === realIndex ? '1' : '0.3';
          dot.style.transform = i === realIndex ? 'scale(1.2)' : 'scale(1)';
        });
      }
    }

    getRealIndex() {
      if (this.infinite && this.totalOriginal > this.slidesPerView) {
        return ((this.currentIndex - this.totalOriginal) % this.totalOriginal + this.totalOriginal) % this.totalOriginal;
      }
      return this.currentIndex;
    }

    goToSlide(index) {
      if (this.isTransitioning) return;
      this.isTransitioning = true;
      this.currentIndex = index;

      const pct = (this.currentIndex * 100) / this.slidesPerView;
      this.track.classList.remove('tc-no-transition');
      this.track.style.transform = 'translateX(-' + pct + '%)';
      this.updateUI();
    }

    jumpToSlide(index) {
      this.currentIndex = index;
      const pct = (this.currentIndex * 100) / this.slidesPerView;
      this.track.classList.add('tc-no-transition');
      this.track.style.transform = 'translateX(-' + pct + '%)';

      // Force reflow
      void this.track.offsetHeight;
      this.track.classList.remove('tc-no-transition');
      this.updateUI();
    }

    next() {
      if (this.isTransitioning) return;

      if (this.infinite && this.totalOriginal > this.slidesPerView) {
        this.goToSlide(this.currentIndex + 1);
      } else {
        const maxIndex = this.totalOriginal - this.slidesPerView;
        if (this.currentIndex < maxIndex) {
          this.goToSlide(this.currentIndex + 1);
        }
      }

      this.restartAutoplay();
    }

    prev() {
      if (this.isTransitioning) return;

      if (this.infinite && this.totalOriginal > this.slidesPerView) {
        this.goToSlide(this.currentIndex - 1);
      } else {
        if (this.currentIndex > 0) {
          this.goToSlide(this.currentIndex - 1);
        }
      }

      this.restartAutoplay();
    }

    onTransitionEnd() {
      this.isTransitioning = false;

      if (this.infinite && this.totalOriginal > this.slidesPerView) {
        // If we scrolled past the end clones, jump back to the real slides
        if (this.currentIndex >= this.totalOriginal + this.totalOriginal) {
          this.jumpToSlide(this.currentIndex - this.totalOriginal);
        }
        // If we scrolled before the start clones, jump forward
        if (this.currentIndex < this.totalOriginal) {
          this.jumpToSlide(this.currentIndex + this.totalOriginal);
        }
      }
    }

    // Touch / Swipe
    onTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
    }

    onTouchEnd(e) {
      this.touchEndX = e.changedTouches[0].screenX;
      const diff = this.touchStartX - this.touchEndX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
    }

    // Autoplay
    startAutoplay() {
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => this.next(), this.autoplaySpeed);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    restartAutoplay() {
      if (this.autoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    }

    // Resize
    onResize() {
      const newSlidesPerView = this.getSlidesPerView();
      if (newSlidesPerView !== this.slidesPerView) {
        this.slidesPerView = newSlidesPerView;
        this.updateCardWidths();
        this.buildDots();

        if (this.infinite && this.totalOriginal > this.slidesPerView) {
          this.currentIndex = this.totalOriginal;
          this.jumpToSlide(this.currentIndex);
        } else {
          this.currentIndex = Math.min(this.currentIndex, this.totalOriginal - this.slidesPerView);
          if (this.currentIndex < 0) this.currentIndex = 0;
          this.jumpToSlide(this.currentIndex);
        }

        this.toggleControls();
      }
    }

    toggleControls() {
      // Hide arrows/dots if not enough slides
      if (this.totalOriginal <= this.slidesPerView) {
        if (this.arrowsContainer) this.arrowsContainer.classList.add('tc-hidden');
        if (this.dotsContainer) this.dotsContainer.classList.add('tc-hidden');
      } else {
        if (this.arrowsContainer && this.showArrows) this.arrowsContainer.classList.remove('tc-hidden');
        if (this.dotsContainer && this.showDots) this.dotsContainer.classList.remove('tc-hidden');
      }
    }

    debounce(fn, wait) {
      let timer;
      return function () {
        clearTimeout(timer);
        timer = setTimeout(fn, wait);
      };
    }
  }

  // Initialize all carousels on the page
  function initAll() {
    document.querySelectorAll('.tc-section[data-carousel]').forEach((el) => {
      if (!el._tcInit) {
        el._tcInit = true;
        new TestimonialCarousel(el);
      }
    });
  }

  // Support Shopify Theme Editor live reload
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on Shopify section events
  document.addEventListener('shopify:section:load', function (e) {
    const section = e.target.querySelector('.tc-section[data-carousel]');
    if (section) {
      section._tcInit = false;
      new TestimonialCarousel(section);
    }
  });
})();
